import { useEffect, useRef, useState } from 'react';

/**
 * Scroll-spy + scroll-progress for the explainer page.
 *
 * The GEO Observatory shell scrolls inside `<main>` (an `overflow-y-auto` ancestor),
 * not the window — so we resolve the nearest scrollable ancestor at mount and
 * drive both the active-section highlight and the progress bar off *that*
 * element's scroll metrics. IntersectionObserver handles "which section is in
 * view"; a throttled scroll listener handles the 0–1 progress fraction.
 *
 * Returns the id of the section closest to the top of the viewport and a
 * 0–1 progress fraction of how far the reader has scrolled through the page.
 */
export interface ScrollSpyResult {
  activeId: string | null;
  progress: number;
}

function findScrollParent(el: HTMLElement | null): HTMLElement | null {
  let node = el?.parentElement ?? null;
  while (node) {
    const { overflowY } = window.getComputedStyle(node);
    if ((overflowY === 'auto' || overflowY === 'scroll') && node.scrollHeight > node.clientHeight) {
      return node;
    }
    node = node.parentElement;
  }
  return null;
}

export function useScrollSpy(
  sectionIds: readonly string[],
  rootRef: React.RefObject<HTMLElement | null>,
): ScrollSpyResult {
  const [activeId, setActiveId] = useState<string | null>(sectionIds[0] ?? null);
  const [progress, setProgress] = useState(0);
  // Track the most-visible section across observer callbacks.
  const ratios = useRef<Map<string, number>>(new Map());

  useEffect(() => {
    const scrollParent = findScrollParent(rootRef.current);
    const root = scrollParent ?? null; // null => viewport

    const elements = sectionIds
      .map((id) => document.getElementById(id))
      .filter((el): el is HTMLElement => el !== null);

    if (elements.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          ratios.current.set(entry.target.id, entry.isIntersecting ? entry.intersectionRatio : 0);
        }
        // Pick the section with the largest visible ratio; ties resolve to
        // the earliest section in registry order (stable nav highlight).
        let best: string | null = null;
        let bestRatio = 0;
        for (const id of sectionIds) {
          const r = ratios.current.get(id) ?? 0;
          if (r > bestRatio) {
            bestRatio = r;
            best = id;
          }
        }
        if (best) setActiveId(best);
      },
      {
        root,
        // Bias the active zone toward the upper third of the viewport so the
        // highlight flips as a heading reaches reading position.
        rootMargin: '-20% 0px -55% 0px',
        threshold: [0, 0.25, 0.5, 0.75, 1],
      },
    );

    elements.forEach((el) => observer.observe(el));

    // Progress: fraction scrolled through the scroll container.
    const target: HTMLElement | Window = scrollParent ?? window;
    let frame = 0;
    const updateProgress = () => {
      frame = 0;
      let scrollTop: number;
      let max: number;
      if (scrollParent) {
        scrollTop = scrollParent.scrollTop;
        max = scrollParent.scrollHeight - scrollParent.clientHeight;
      } else {
        scrollTop = window.scrollY;
        max = document.documentElement.scrollHeight - window.innerHeight;
      }
      setProgress(max > 0 ? Math.min(1, Math.max(0, scrollTop / max)) : 0);
    };
    const onScroll = () => {
      if (frame === 0) frame = window.requestAnimationFrame(updateProgress);
    };
    target.addEventListener('scroll', onScroll, { passive: true });
    updateProgress();

    return () => {
      observer.disconnect();
      ratios.current.clear();
      target.removeEventListener('scroll', onScroll);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, [sectionIds, rootRef]);

  return { activeId, progress };
}
